// frontend/src/pages/AnalysisPage/sections/PartContext/PartContext.js
// Purpose: Display and manage part contextualization with default values
// Desired outcome: Show parts with missing data filled in with category defaults, with readable field names and descriptions

import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectCurrentFile,
  selectContextualizedParts,
  selectProgress,
  selectBootstrapComplete,
  selectPartMatches,
  selectFieldMappings,
  bootstrapAnalysisData,
  fetchContextualizedParts,
  confirmContextualizedParts,
  updateContextualizedPart,
  fetchFieldMappings
} from '../../../../state/analysis/analysisSlice';
import CompletenessIndicator from '../../../../components/CompletenessIndicator/CompletenessIndicator';
import logger from '../../../../utils/logger';
import '../sections.css';
import './PartContext.css';

const PartContext = () => {
  const dispatch = useDispatch();
  const currentFile = useSelector(selectCurrentFile);
  const contextualizedParts = useSelector(selectContextualizedParts);
  const partMatches = useSelector(selectPartMatches);
  const progress = useSelector(selectProgress);
  const bootstrapComplete = useSelector(selectBootstrapComplete);
  const fieldMappingsData = useSelector(selectFieldMappings);
  
  // Local state for UI
  const [loading, setLoading] = useState(true);
  const [error, setLocalError] = useState(null);
  const [editedParts, setEditedParts] = useState({});
  const [filterCategory, setFilterCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'card'
  const [fieldDescriptions, setFieldDescriptions] = useState({});
  
  // Use ref to prevent multiple API calls
  const fetchAttempted = useRef(false);
  
  // Bootstrap process
  useEffect(() => {
    if (!bootstrapComplete) {
      dispatch(bootstrapAnalysisData());
    }
  }, [dispatch, bootstrapComplete]);

  // Fetch field mappings if not already loaded
  useEffect(() => {
    if (!fieldMappingsData) {
      dispatch(fetchFieldMappings());
    } else {
      // Process field descriptions from the field mappings
      const descriptions = {};
      if (fieldMappingsData && fieldMappingsData.fields) {
        Object.entries(fieldMappingsData.fields).forEach(([category, fields]) => {
          fields.forEach(field => {
            descriptions[field.name] = field.description;
          });
        });
      }
      setFieldDescriptions(descriptions);
    }
  }, [dispatch, fieldMappingsData]);

  // Fetch contextualized parts
  useEffect(() => {
    // Skip if no file selected or we've already attempted fetch
    if (!currentFile?.fileID || fetchAttempted.current) {
      return;
    }
    
    // Check if we already have contextualized parts
    if (contextualizedParts && contextualizedParts.length > 0) {
      setLoading(false);
      fetchAttempted.current = true;
      return;
    }
    
    // Need to wait for bootstrap and part matches
    if (!bootstrapComplete || !partMatches || partMatches.length === 0) {
      if (bootstrapComplete && (!partMatches || partMatches.length === 0)) {
        setLocalError('No part matches found. Please complete the part matching step first.');
        setLoading(false);
      }
      return;
    }
    
    // Mark as attempted to prevent multiple calls
    fetchAttempted.current = true;
    
    // Fetch contextualized parts
    dispatch(fetchContextualizedParts(currentFile.fileID))
      .unwrap()
      .then(() => {
        setLoading(false);
      })
      .catch((error) => {
        setLocalError(`Error fetching contextualized parts: ${error}`);
        setLoading(false);
      });
  }, [dispatch, currentFile, bootstrapComplete, contextualizedParts, partMatches]);

  // Get a user-friendly display for a field name
  const getFieldDisplay = (fieldName) => {
    // Get the description from our fieldDescriptions state
    const description = fieldDescriptions[fieldName] || '';
    
    return {
      fieldName,
      displayName: fieldName,
      description
    };
  };

  // Handle field value change
  const handleFieldChange = (partID, field, value) => {
    // Update local edited state
    setEditedParts(prev => ({
      ...prev,
      [partID]: {
        ...(prev[partID] || {}),
        [field]: value
      }
    }));
    
    // Dispatch action to update Redux state
    dispatch(updateContextualizedPart({
      partID,
      mappedColumns: {
        [field]: value
      }
    }));
  };

  // Confirm all contextualized parts and proceed to next step
  const handleConfirmParts = () => {
    if (!currentFile || !currentFile.fileID) {
      setLocalError('No file selected');
      return;
    }
    
    if (!contextualizedParts || contextualizedParts.length === 0) {
      setLocalError('No parts to confirm');
      return;
    }
    
    dispatch(confirmContextualizedParts({
      fileID: currentFile.fileID,
      confirmedContextualizedParts: contextualizedParts
    }))
      .unwrap()
      .then(() => {
        logger.info('Contextualized parts confirmed successfully');
      })
      .catch((error) => {
        setLocalError(error.message || 'Error confirming contextualized parts');
        logger.error('Error confirming contextualized parts', error);
      });
  };

  // Reset and retry fetching
  const handleRetry = () => {
    fetchAttempted.current = false;
    setLoading(true);
    setLocalError(null);
  };

  // Filter parts based on criteria
  const getFilteredParts = () => {
    if (!contextualizedParts) return [];
    
    return contextualizedParts.filter(part => {
      // Filter by category
      if (filterCategory !== 'all' && part.matchedCategory !== filterCategory) {
        return false;
      }
      
      // Filter by search term
      if (searchTerm && !part.originalName.toLowerCase().includes(searchTerm.toLowerCase())) {
        return false;
      }
      
      return true;
    });
  };

  // Get unique categories for filter dropdown
  const getUniqueCategories = () => {
    if (!contextualizedParts) return [];
    
    const categories = [...new Set(contextualizedParts.map(part => part.matchedCategory))];
    return categories.sort();
  };

  // Get all unique fields from all parts
  const getAllFields = () => {
    if (!contextualizedParts || contextualizedParts.length === 0) return [];
    
    const fieldsSet = new Set();
    
    // Collect all field names from all parts
    contextualizedParts.forEach(part => {
      if (part.mappedColumns) {
        Object.keys(part.mappedColumns).forEach(field => fieldsSet.add(field));
      }
    });
    
    // Convert to array and sort
    return Array.from(fieldsSet).sort();
  };

  // Rendering states
  if (loading || progress.status === 'loading') {
    return <div className="loading-indicator">Initializing analysis...</div>;
  }

  // If there's an error
  if (error) {
    return (
      <div className="part-context-section">
        <h2>Part Contextualization</h2>
        <div className="error-message">{error}</div>
        <button className="secondary-button" onClick={handleRetry}>Retry</button>
      </div>
    );
  }

  // Filter parts for display
  const filteredParts = getFilteredParts();
  const uniqueCategories = getUniqueCategories();
  const allFields = getAllFields();

  // If no contextualized parts found
  if (!filteredParts || filteredParts.length === 0) {
    return (
      <div className="part-context-section">
        <h2>Part Contextualization</h2>
        <div className="no-parts">No parts found. Please complete the part matching step first.</div>
      </div>
    );
  }

  return (
    <div className="part-context-section">
      <div className="part-context-header">
        <h2>Part Contextualization</h2>
        <div className="view-mode-buttons">
          <button 
            className={`view-mode-button ${viewMode === 'card' ? 'active' : ''}`} 
            onClick={() => setViewMode('card')}
          >
            Card View
          </button>
          <button 
            className={`view-mode-button ${viewMode === 'table' ? 'active' : ''}`} 
            onClick={() => setViewMode('table')}
          >
            Table View
          </button>
        </div>
      </div>
      
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}
      
      <div className="instructions">
        <p>
          Review the parts below. Missing data has been filled with default values based on the part category.
          You can edit any field before confirming.
        </p>
      </div>
      
      {/* Filtering controls */}
      <div className="filter-controls">
        <div className="filter-item">
          <label htmlFor="categoryFilter">Filter by Category:</label>
          <select
            id="categoryFilter"
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Categories</option>
            {uniqueCategories.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>
        </div>
        
        <div className="filter-item">
          <label htmlFor="searchFilter">Search by Name:</label>
          <input
            id="searchFilter"
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by part name..."
            className="search-input"
          />
        </div>
      </div>
      
      {/* Parts display */}
      {viewMode === 'card' ? (
        // Card View
        <div className="parts-container">
          {filteredParts.length === 0 ? (
            <div className="no-parts">No parts found matching your filters</div>
          ) : (
            filteredParts.map((part) => (
              <div key={part.partID} className="part-card">
                <div className="part-header">
                  <h3>{part.originalName}</h3>
                  <span className="category-badge">
                    {part.matchedCategory}
                  </span>
                </div>
                
                <CompletenessIndicator score={part.completenessScore} />
                
                <div className="part-fields">
                  {part.mappedColumns && Object.entries(part.mappedColumns).map(([field, value]) => {
                    const { displayName, description } = getFieldDisplay(field);
                    return (
                      <div key={field} className="field-row">
                        <label className="field-label">
                          <span className="field-name">{displayName}</span>
                          <span className="field-description">{description}</span>
                        </label>
                        <input
                          type="text"
                          value={editedParts[part.partID]?.[field] !== undefined ? editedParts[part.partID][field] : value}
                          onChange={(e) => handleFieldChange(part.partID, field, e.target.value)}
                          className="field-input"
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>
      ) : (
        // Table View
        <div className="parts-table-container">
          {filteredParts.length === 0 ? (
            <div className="no-parts">No parts found matching your filters</div>
          ) : (
            <table className="parts-table">
              <thead>
                <tr>
                  <th>Part Name</th>
                  <th>Category</th>
                  <th>Completeness</th>
                  {allFields.map(field => {
                    const { displayName, description } = getFieldDisplay(field);
                    return (
                      <th key={field}>
                        <div className="table-field-header">
                          <span className="table-field-name">{displayName}</span>
                          <span className="table-field-description">{description}</span>
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {filteredParts.map(part => (
                  <tr key={part.partID}>
                    <td>{part.originalName}</td>
                    <td>{part.matchedCategory}</td>
                    <td>
                      <CompletenessIndicator score={part.completenessScore} />
                    </td>
                    {allFields.map(field => (
                      <td key={field}>
                        <input
                          type="text"
                          value={
                            editedParts[part.partID]?.[field] !== undefined 
                              ? editedParts[part.partID][field] 
                              : (part.mappedColumns?.[field] || '')
                          }
                          onChange={(e) => handleFieldChange(part.partID, field, e.target.value)}
                          className="table-field-input"
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
      
      <div className="actions">
        <button 
          className="primary-button"
          onClick={handleConfirmParts}
          disabled={progress.status === 'loading'}
        >
          {progress.status === 'loading' ? 'Confirming...' : 'Confirm & Continue'}
        </button>
      </div>
    </div>
  );
};

export default PartContext;